import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { ChatContext, EntityContext, ScreenContext } from '../types';
import {
  parseMooPolicyParam,
  type MooPolicyDeepLink,
} from '../components/sources/SourcePreviewHandler';

/**
 * Ambient Ask Moo state that lives OUTSIDE the chat panel.
 *
 * The panel itself (`@cowbell/moo`) owns its open/layout state, agent
 * selection, conversation, and route-derived screen context. What remains here
 * is the state that non-chat surfaces need to reach: the manually-curated
 * context chips (a policies-listing row can push an entity chip), and the
 * policy deep link captured from the URL before any route effect rewrites it.
 */
export interface AskMooContextValue {
  contexts: ChatContext[];
  addContext: (ctx: ChatContext) => void;
  removeContext: (index: number) => void;
  clearContexts: () => void;
  clearAttachments: () => void;
  /** Toggle the `pinned` flag on the context at `index`. No-op for types that
   *  don't support pinning (document, selection). */
  togglePinContext: (index: number) => void;
  setContexts: Dispatch<SetStateAction<ChatContext[]>>;
  /**
   * Policy drawer deep link (``?mooPolicy=<product>:<id>``) captured from the
   * URL at app-root render time — before any route component's effect can
   * rewrite the query string (e.g. the dashboard's before/after date range).
   * The Ask Moo integration consumes this on mount to reopen the drawer.
   */
  initialPolicyDeepLink: MooPolicyDeepLink | null;
  /**
   * Clears {@link initialPolicyDeepLink} once the integration has opened the
   * drawer for it. The provider outlives the integration component, so without
   * consuming it a remount would re-run the mount effect and reopen a drawer
   * the user already closed.
   */
  clearInitialPolicyDeepLink: () => void;
}

export const AskMooContext = createContext<AskMooContextValue | null>(null);

interface Props {
  children: React.ReactNode;
}

export default function AskMooProvider({ children }: Props) {
  // Captured synchronously during the first render (a useState initializer runs
  // in the render phase, before any child route effect commits), so the policy
  // deep-link param survives the dashboard's query-string rewrite.
  const [initialPolicyDeepLink, setInitialPolicyDeepLink] =
    useState<MooPolicyDeepLink | null>(() =>
      typeof window === 'undefined'
        ? null
        : parseMooPolicyParam(window.location.search)
    );
  const clearInitialPolicyDeepLink = useCallback(
    () => setInitialPolicyDeepLink(null),
    []
  );

  const [contexts, setContexts] = useState<ChatContext[]>([]);

  const addContext = useCallback((ctx: ChatContext) => {
    setContexts((prev) => {
      if (ctx.type === 'screen') {
        const incoming = ctx as ScreenContext;
        const incomingDataJson = JSON.stringify(incoming.entityData);
        const sameScreen = (c: ChatContext) =>
          c.type === 'screen' &&
          (c as ScreenContext).view === incoming.view &&
          (c as { entityId?: string }).entityId === incoming.entityId &&
          (c as { subTab?: string }).subTab === incoming.subTab &&
          JSON.stringify((c as ScreenContext).entityData) === incomingDataJson;
        const hasPinnedScreen = prev.some(
          (c) => c.type === 'screen' && (c as ScreenContext).pinned
        );
        if (!hasPinnedScreen) {
          // When no pinned screen is involved, a re-add of the same screen
          // (matching view, entityId, subTab, and entityData) returns `prev`
          // unchanged so that referential equality and chip order are
          // preserved.
          if (prev.some(sameScreen)) return prev;
          return [...prev.filter((c) => c.type !== 'screen'), ctx];
        }
        // A pinned screen exists. Drop transient screens so that returning
        // to a pinned route also evicts any stale unpinned screen chip
        // that was added in the meantime.
        const filtered = prev.filter(
          (c) => c.type !== 'screen' || (c as ScreenContext).pinned
        );
        if (filtered.some(sameScreen)) {
          // Already represented by a pinned screen chip — return `prev`
          // only if nothing was filtered out (preserve referential equality).
          return filtered.length === prev.length ? prev : filtered;
        }
        return [...filtered, ctx];
      }

      if (ctx.type === 'entity') {
        const ctxId = (ctx as { entityId?: string }).entityId;
        if (
          ctxId &&
          prev.some(
            (existing) =>
              existing.type === 'entity' &&
              (existing as { entityId?: string }).entityId === ctxId
          )
        ) {
          return prev;
        }
      }

      return [...prev, ctx];
    });
  }, []);

  const removeContext = useCallback((index: number) => {
    setContexts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearContexts = useCallback(() => {
    setContexts([]);
  }, []);

  const togglePinContext = useCallback((index: number) => {
    setContexts((prev) => {
      const target = prev[index];
      if (!target || (target.type !== 'entity' && target.type !== 'screen')) {
        return prev;
      }
      return prev.map((c, i) => {
        if (i !== index) return c;
        if (c.type === 'screen') {
          const next: ScreenContext = { ...c, pinned: !c.pinned };
          return next;
        }
        if (c.type === 'entity') {
          const next: EntityContext = { ...c, pinned: !c.pinned };
          return next;
        }
        return c;
      });
    });
  }, []);

  const clearAttachments = useCallback(() => {
    // Pinned chips survive a "new chat" / clearAttachments call.
    setContexts((prev) =>
      prev.filter(
        (c) =>
          (c.type === 'entity' || c.type === 'screen') &&
          (c as { pinned?: boolean }).pinned
      )
    );
  }, []);

  const value = useMemo<AskMooContextValue>(
    () => ({
      contexts,
      addContext,
      removeContext,
      clearContexts,
      clearAttachments,
      togglePinContext,
      setContexts,
      initialPolicyDeepLink,
      clearInitialPolicyDeepLink,
    }),
    [
      contexts,
      addContext,
      removeContext,
      clearContexts,
      clearAttachments,
      togglePinContext,
      initialPolicyDeepLink,
      clearInitialPolicyDeepLink,
    ]
  );

  return (
    <AskMooContext.Provider value={value}>{children}</AskMooContext.Provider>
  );
}
